import { ChevronDown, Circle, Clock, ArrowUp, Check, Inbox, User, UserX, Users } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { useFilter, type FilterOption, type AssignmentOption } from '@/contexts/FilterContext'

interface OptionConfig {
  label: string
  icon: React.ReactNode
}

const statusOptions: Record<FilterOption, OptionConfig> = {
  unresolved: { label: 'Unresolved', icon: <ArrowUp className="size-4" /> },
  resolved: { label: 'Resolved', icon: <Check className="size-4" /> },
  all: { label: 'All', icon: <Inbox className="size-4" /> },
  unread: { label: 'Unread', icon: <Circle className="size-4" /> },
  most_recent: { label: 'Most Recent', icon: <ArrowUp className="size-4" /> },
  longest_waiting: { label: 'Longest Waiting', icon: <Clock className="size-4" /> },
}

const assignmentOptions: Record<AssignmentOption, OptionConfig> = {
  everyone: { label: 'Everyone', icon: <Users className="size-4" /> },
  mine: { label: 'Mine', icon: <User className="size-4" /> },
  unassigned: { label: 'Unassigned', icon: <UserX className="size-4" /> },
}

export function ConversationFilter() {
  const { currentFilter, currentAssignment, setFilter, setAssignment } = useFilter()
  const statusConfig = statusOptions[currentFilter] ?? statusOptions.unresolved
  const assignmentConfig = assignmentOptions[currentAssignment] ?? assignmentOptions.everyone

  const buttonLabel =
    currentAssignment === 'everyone'
      ? statusConfig.label
      : `${statusConfig.label} · ${assignmentConfig.label}`

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 border-sidebar-secondary-accent/30 bg-sidebar-secondary hover:bg-sidebar-secondary-accent/50 text-sidebar-secondary-foreground"
        >
          {buttonLabel}
          <ChevronDown className="size-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Status
        </DropdownMenuLabel>
        {(Object.keys(statusOptions) as FilterOption[]).map((key) => {
          const config = statusOptions[key]
          const isActive = key === currentFilter
          return (
            <DropdownMenuItem
              key={key}
              onClick={() => setFilter(key)}
              className={cn('cursor-pointer gap-2', isActive && 'bg-accent text-accent-foreground')}
            >
              {config.icon}
              <span className="flex-1">{config.label}</span>
              {isActive && <div className="size-1.5 rounded-full bg-primary" />}
            </DropdownMenuItem>
          )
        })}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Assignment
        </DropdownMenuLabel>
        {(Object.keys(assignmentOptions) as AssignmentOption[]).map((key) => {
          const config = assignmentOptions[key]
          const isActive = key === currentAssignment
          return (
            <DropdownMenuItem
              key={key}
              onClick={() => setAssignment(key)}
              className={cn('cursor-pointer gap-2', isActive && 'bg-accent text-accent-foreground')}
            >
              {config.icon}
              <span className="flex-1">{config.label}</span>
              {isActive && <div className="size-1.5 rounded-full bg-primary" />}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
